import { ShieldCheck, RefreshCw, XCircle, CreditCard, CheckCircle2 } from 'lucide-react';
import SEO from '../components/SEO';

export default function ReturnsPage() {
    return (
        <div className="min-h-screen bg-white">
            <SEO title="Returns Policy" description="NSO India Returns and Refund Policy" noIndex={true} />
            {/* Hero Section */}
            <section className="bg-surface py-16 md:py-24">
                <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
                    <span className="text-sm uppercase tracking-widest text-primary font-body font-semibold mb-4 block">
                        Confidence in every purchase
                    </span>
                    <h1 className="font-heading text-4xl md:text-6xl text-secondary mb-6">
                        Returns & <span className="text-primary">Exchanges</span>
                    </h1>
                    <p className="text-lg text-muted-foreground font-body max-w-2xl mx-auto leading-relaxed">
                        Not happy with your product? No problem. Our hassle-free return policy has you covered.
                    </p>
                </div>
            </section>

            <section className="py-16 md:py-24">
                <div className="max-w-7xl mx-auto px-4 md:px-8">
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20 text-center">
                        <div className="p-6">
                            <div className="w-16 h-16 bg-surface rounded-full flex items-center justify-center mx-auto mb-6">
                                <span className="text-2xl font-heading text-primary">01</span>
                            </div>
                            <h3 className="font-heading text-xl text-secondary mb-3">Initiate</h3>
                            <p className="text-sm text-muted-foreground font-body">Go to your account and select the order you wish to return within 7 days.</p>
                        </div>
                        <div className="p-6">
                            <div className="w-16 h-16 bg-surface rounded-full flex items-center justify-center mx-auto mb-6">
                                <span className="text-2xl font-heading text-primary">02</span>
                            </div>
                            <h3 className="font-heading text-xl text-secondary mb-3">Pack</h3>
                            <p className="text-sm text-muted-foreground font-body">Pack the product in its original box with all tags and invoice intact.</p>
                        </div>
                        <div className="p-6">
                            <div className="w-16 h-16 bg-surface rounded-full flex items-center justify-center mx-auto mb-6">
                                <span className="text-2xl font-heading text-primary">03</span>
                            </div>
                            <h3 className="font-heading text-xl text-secondary mb-3">Pickup</h3>
                            <p className="text-sm text-muted-foreground font-body">Our courier partner will pick up the package from your address for free.</p>
                        </div>
                        <div className="p-6">
                            <div className="w-16 h-16 bg-surface rounded-full flex items-center justify-center mx-auto mb-6">
                                <span className="text-2xl font-heading text-primary">04</span>
                            </div>
                            <h3 className="font-heading text-xl text-secondary mb-3">Refund</h3>
                            <p className="text-sm text-muted-foreground font-body">Once received and inspected, we will process your refund instantly.</p>
                        </div>
                    </div>

                    <div className="grid lg:grid-cols-2 gap-16 items-start">
                        <div className="space-y-12">
                            <div className="animate-fade-in">
                                <h2 className="font-heading text-3xl text-secondary mb-6 flex items-center gap-3">
                                    <ShieldCheck className="w-8 h-8 text-primary" />
                                    Return Eligibility
                                </h2>
                                <ul className="space-y-4 font-body">
                                    {[
                                        'Products must be returned within 7 days of delivery.',
                                        'Items must be in original, unwashed, and unused condition.',
                                        'All original tags, warranty cards, and accessories must be present.',
                                        'The protective film on smartphones must not be removed.',
                                        'Hygiene-sealed items (if any) must remain sealed.'
                                    ].map((text, i) => (
                                        <li key={i} className="flex items-start gap-3">
                                            <CheckCircle2 className="w-5 h-5 text-success mt-1 flex-shrink-0" />
                                            <span className="text-muted-foreground text-lg">{text}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="animate-fade-in">
                                <h2 className="font-heading text-3xl text-secondary mb-6 flex items-center gap-3">
                                    <XCircle className="w-8 h-8 text-destructive" />
                                    Non-Returnable Items
                                </h2>
                                <p className="text-lg text-muted-foreground font-body mb-4">
                                    Certain items cannot be returned due to hygiene or security reasons:
                                </p>
                                <div className="bg-destructive/5 rounded-3xl p-6 border border-destructive/10">
                                    <ul className="space-y-3 font-body text-secondary">
                                        <li>• Digital Gift Cards (once emailed)</li>
                                        <li>• Physical Gift Cards (if the scratch code is revealed)</li>
                                        <li>• Personalized/Customized eyewear frames</li>
                                        <li>• Refurbished or Clearance Sale items</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className="bg-secondary rounded-[2.5rem] p-10 text-white space-y-8 sticky top-24 shadow-2xl">
                            <h2 className="font-heading text-3xl">Refund Process</h2>
                            <div className="space-y-6">
                                <div className="flex gap-4">
                                    <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                                        <CreditCard className="w-6 h-6 text-primary" />
                                    </div>
                                    <div>
                                        <h4 className="font-body font-semibold text-lg">Online Payment</h4>
                                        <p className="text-white/70 text-sm">Refund will be credited to the original payment source within 5-10 business days.</p>
                                    </div>
                                </div>
                                <div className="flex gap-4">
                                    <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                                        <RefreshCw className="w-6 h-6 text-primary" />
                                    </div>
                                    <div>
                                        <h4 className="font-body font-semibold text-lg">Cash on Delivery</h4>
                                        <p className="text-white/70 text-sm">Refund will be sent to your bank account via NEFT. You will receive a link to securely enter bank details.</p>
                                    </div>
                                </div>
                            </div>
                            <div className="pt-6 border-t border-white/10">
                                <p className="font-body italic text-white/50 text-sm">
                                    Questions? Our returns team is available at returns@Nso-India.com
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
